/**
 * Engagement Chat API Service - ISOLATED for enterprise engagement messaging
 * Handles communication with B2B backend via Analytics Service proxy
 * Completely independent from existing conversation API services
 */

interface EngagementConversation {
  id: number;
  b2b_user_id: number;
  b2b_series_user_id?: number;
  b2b_ai_friend_phone: string;
  b2b_ai_friend_name: string;
  b2b_user_phone?: string;
  target_series_user_id: string;
  target_user_name: string;
  linq_chat_id: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface B2BUserWithEngagements {
  b2b_user: {
    id: number;
    email: string;
    series_user_id: number;
    credits: number;
    created_at: string;
  };
  engagements: Array<{
    engagement_id: string;
    target_user: {
      series_user_id: string;
      name: string;
    };
    conversation: {
      id: number;
      b2b_ai_friend_name: string;
      b2b_ai_friend_phone: string;
      linq_chat_id: number | null;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    } | null;  // Conversation might not exist yet
    messaging: {
      message_count: number;
      unread_from_b2b_user: number;
      has_messages: boolean;
      last_updated: string | null;
    };
  }>;
}

interface AnalyticsDashboardView {
  b2b_users: B2BUserWithEngagements[];
  summary: {
    total_b2b_users: number;
    total_engagements: number;
    engagement_conversations_created: number;
    conversations_with_messages: number;
  };
}


interface EngagementMessage {
  id: number;
  conversation_id: number;
  sender_type: 'b2b_user' | 'analytics_operator';
  content: string;
  linq_message_id: number | null;
  sent_via_imessage: boolean;
  created_at: string;
}

interface SendReplyResponse {
  success: boolean;
  message_id: number;
  conversation_id: number;
  note?: string;
}


// Campaign Chat Interfaces
interface CampaignConversation {
  id: number;
  campaign_id: number;
  b2b_user_id: number;
  b2b_user_email: string;
  b2b_user_phone: string;
  analytics_chat_id: number;
  analytics_phone_number: string;
  selected_match_group: string;
  primary_user_id: number;
  message_count: number;
  last_message_at: string | null;
  status: string;
  campaign_query: string;
  created_at: string;
  updated_at: string;
}

interface CampaignMessage {
  id: number;
  conversation_id: number;
  message_type: 'user' | 'analytics';
  sender_phone: string;
  content: string;
  linq_message_id: string | null;
  source: 'web' | 'sms';
  delivered: boolean;
  delivered_at: string | null;
  created_at: string;
}


class EngagementChatApiService {
  /**
   * Get base URL for API requests
   * ISOLATED - uses same base as Analytics API but different endpoints
   */
  private static getBaseUrl(): string {
    // In production, use relative URLs to go through Vercel proxy
    if (import.meta.env.PROD) {
      return '';  // Relative URLs route through Vercel proxy
    }
    
    // In development, use configured URL or localhost
    return import.meta.env.VITE_ANALYTICS_API_BASE_URL || 'http://localhost:8080';
  }
  
  /**
   * Get headers for API requests (use Authorization header in dev)
   */
  private static getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('series_analytics_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch {}
    return headers;
  }
  
  /**
   * Generic GET request helper
   */
  private static async get<T>(path: string): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const url = `${cleanBaseUrl}${path}`;
    
    const response = await fetch(url, {
      headers: this.getHeaders(),
      credentials: 'include'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GET ${path} failed: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  }
  
  /**
   * Get Analytics Dashboard view with B2B users, engagements, and messaging data
   * NEW ENDPOINT - complete hierarchical view with message counts
   */
  static async getAnalyticsDashboardView(): Promise<AnalyticsDashboardView> {
    console.log('Fetching Analytics Dashboard engagement view...');
    return this.get<AnalyticsDashboardView>('/api/v1/analytics/engagement-dashboard-view');
  }
  
  /**
   * Get all active enterprise engagement conversations (flat list)
   * ISOLATED - does not interfere with normal conversation loading
   */
  static async getEngagementConversations(): Promise<EngagementConversation[]> {
    console.log('Fetching enterprise engagement conversations...');
    return this.get<EngagementConversation[]>('/api/v1/analytics/engagement-conversations');
  }
  
  /**
   * Get messages for a specific engagement conversation
   * ISOLATED - separate from normal message endpoints
   */
  static async getEngagementMessages(conversationId: number): Promise<EngagementMessage[]> {
    console.log(`Fetching messages for engagement conversation ${conversationId}...`);
    return this.get<EngagementMessage[]>(
      `/api/v1/analytics/engagement-conversations/${conversationId}/messages`
    );
  }
  
  /**
   * Send reply to B2B user via enterprise engagement
   * ISOLATED - routes through B2B backend to iMessage service
   * 
   * @param conversationId - Engagement conversation ID
   * @param content - Message content to send
   * @returns Response with success status and message ID
   */
  static async sendReplyToB2BUser(
    conversationId: number, 
    content: string
  ): Promise<SendReplyResponse> {
    const baseUrl = this.getBaseUrl();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    const params = new URLSearchParams({ content });
    const url = `${cleanBaseUrl}/api/v1/analytics/engagement-conversations/${conversationId}/reply?${params.toString()}`;
    
    console.log(`Sending reply to engagement conversation ${conversationId}: ${content.length} chars`);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send reply: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`Reply sent successfully: message_id=${result.message_id}`);
    
    return result;
  }
  
  // ========================================
  // CAMPAIGN CHAT ENDPOINTS
  // ========================================
  
  /**
   * Get all campaign conversations for Analytics Dashboard
   */
  static async getAllCampaignConversations(): Promise<CampaignConversation[]> {
    console.log('Fetching all campaign conversations...');
    return this.get<CampaignConversation[]>('/api/v1/analytics/campaign-conversations/all');
  }
  
  /**
   * Get messages for a campaign conversation
   */
  static async getCampaignMessages(conversationId: number, limit: number = 100): Promise<CampaignMessage[]> {
    console.log(`Fetching messages for campaign conversation ${conversationId}...`);
    return this.get<CampaignMessage[]>(
      `/api/v1/analytics/campaign-conversations/${conversationId}/messages?limit=${limit}`
    );
  }
  
  /**
   * Send reply to B2B user in campaign chat
   */
  static async sendCampaignReply(conversationId: number, content: string): Promise<SendReplyResponse> {
    const baseUrl = this.getBaseUrl();
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    const params = new URLSearchParams({ content });
    const url = `${cleanBaseUrl}/api/v1/analytics/campaign-conversations/${conversationId}/reply?${params.toString()}`;
    
    console.log(`Sending campaign reply to conversation ${conversationId}: ${content.length} chars`);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send campaign reply: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`Campaign reply sent successfully: message_id=${result.message_id}`);
    
    return result;
  }
}

export default EngagementChatApiService;
export type { 
  EngagementConversation, 
  EngagementMessage, 
  SendReplyResponse, 
  B2BUserWithEngagements,
  AnalyticsDashboardView,
  CampaignConversation,
  CampaignMessage
};

